/// <reference path="./.typedefs.js" />
import MggpPlayer from 'mggp-player';
import { countryCodeToLangCode } from "mggp-helper-languages";
import SRTProvider from './lib/SRTProvider.js';
import SubtitleEmitter from './lib/SubEmitter.js';
import createLangFilter from './lib/createLangFilter.js';
import { getMatchedOrFirst } from './lib/getMatchedOrFirst.js';
import { singleHandlerFactory } from './lib/singleHandlerFactory.js';
import { deferCall } from './lib/deferCall.js';
import { OFF, SRT_TYPE } from './constants';

export default MggpPluginSubtitles;

MggpPluginSubtitles.prototype = Object.create(MggpPlayer.Plugin.prototype);
MggpPluginSubtitles.prototype.constructor = MggpPluginSubtitles;

MggpPluginSubtitles.prototype.version = process.env.VERSION;
MggpPluginSubtitles.prototype.build = process.env.BUILD;
MggpPluginSubtitles.prototype.name = process.env.NAME;

/**
 * experimental, need to move to Plugin if valid
 * @this MggpPluginSubtitles
 * @param {string} propName
 * @param {any} propValue
 */
MggpPluginSubtitles.prototype.expose = function (propName, propValue) {
  this.player.set(propName, propValue);
};

/**
 * @constructor
 * @augments MggpPlayer.Plugin
 * @name MggpPluginSubtitles
 */
function MggpPluginSubtitles() {
  MggpPlayer.Plugin.apply(this, arguments);

  const self = this;
  let _player = self.player;

  const _localeLanguage = countryCodeToLangCode(self.config.lang) || self.config.lang;
  let _preferredLang = self.config?.playerOptions?.defaultSubtitles || OFF;
  let _preferredRequiredLang = OFF;
  let _currentSubtitlesLang = self.config.defaultSubtitles || OFF;
  let _enable = _currentSubtitlesLang !== OFF;
  let _requiredMode = false;
  /**
   * @type {string[]}
   * @private
   */
  let _exclusions = [];

  /**
   * @param {Subtitle} subtitle
   * @return {boolean}
   */
  function isSubtitleAllowed(subtitle) {
    return undefined === _exclusions.find((e) => e === subtitle.lang_tag);
  }

  /**
   *
   * @type {Subtitle[]}
   * @private
   */
  let _subtitles = [];

  const srtProvider = SRTProvider();

  const offCaption = self.config.i18nMgg && typeof self.config.i18nMgg == 'function' ? self.config.i18nMgg('subtitles_off') : OFF;
  /**
   *
   * @type {Partial<Subtitle>}
   */
  const offOption = {
    lang_original: offCaption,
    display_name: offCaption,
    lang: OFF,
    lang_tag: OFF,
    index: -1,
  };

  this.dispose = function () {
    handlers.forEach((stopListen) => stopListen());
    handlers.length = 0;
    setOff();

    _player = null;
    srtProvider.reset();
  };
  /**
   *
   * @type {[string, function(PlayerEvent): void][]}
   */
  const listeners = [
    ['property:videoInfo', onVideoInfoChange],
    ['player:mediaSeekEnd', onMediaSeekEnd],
    ['player:subtitlesNeedChange', onSubtitlesNeedChange],
    ['player:stop', onPlayerStop],
    [
      /**
       * @see MggpPluginAudio.events.REQUIRE_SUBTITLES
       */
      'AUDIO:REQUIRE_SUBTITLES', onSubtitlesRequire],
  ];

  let handlers = listeners.map((pair) => newHandler(pair[0], pair[1]));
  const listenPositionUpdate = singleHandlerFactory('property:position', newHandler);

  /**
   *
   * @param {PlayerEvent} event
   */
  function onVideoInfoChange(event) {
    const _videoInfo = event.data;
    const subtitles = _videoInfo['subtitles'] || [];
    _exclusions = [];
    _subtitles = subtitles
      .filter(item => item.type === SRT_TYPE);

    srtProvider.reset();
    apply();
  }

  /**
   * TODO:
   *  Not sure: is it still required? Didn't the subEmitter do the same?
   */
  function onMediaSeekEnd() {
    subtitlesTextChange('');
  }

  function onSubtitlesNeedChange(event) {
    const targetLang = event.data;
    if (targetLang === _currentSubtitlesLang) {
      return;
    }

    if (_requiredMode && targetLang === 'off') {
      return;
    }

    _currentSubtitlesLang = targetLang;

    // Save "user choice" in any mode to apply this one in ARS mode
    _preferredRequiredLang = targetLang;

    _player.emit('preference:subtitles', _currentSubtitlesLang);
    _preferredLang = targetLang;

    apply();
  }

  function onPlayerStop() {
    srtProvider.reset();
    setOff();
    _preferredLang = OFF;
    _currentSubtitlesLang = OFF;
  }

  /**
   * @param {PlayerEvent} event
   * @listens MggpPluginAudio~requireSubtitles
   */
  function onSubtitlesRequire(event) {
    const {
      data: {
        lang,
        require,
      },
    } = event;

    _requiredMode = require;

    if (require) {
      const byLang = createLangFilter(lang);
      const tagToExclude = _subtitles
        .filter(byLang)
        .map(subs => subs.lang_tag);

      _exclusions = [OFF].concat(tagToExclude);

      _currentSubtitlesLang = _preferredRequiredLang;
    } else {
      _exclusions = [];
      _currentSubtitlesLang = _preferredLang;
    }
    apply();
  }

  const apply = deferCall(applyNow);

  function applyNow() {
    setOff();

    const availableSubtitles = [offOption].concat(_subtitles).filter(isSubtitleAllowed);
    const subtitlesPreferences = [
      // in ARS mode, having manual user's selection,
      // we prefer rule "user choice" over rule "UI lang",
      // that rule will "play" after and __only__ after user chose some subtitles manually,
      // due to initially _preferredRequiredLang = "off" and will not be applied in ARS mode.
      _requiredMode && _preferredRequiredLang,
      // Next preference in ARS mode is "UI lang"
      _requiredMode && _localeLanguage,
      // In non-ARS mode we have the only preference, previously saved "user choice"
      _preferredLang,
      // fallback to first in list: "off" (if necessary) - via `getMatchedOrFirst`
    ];
    const subtitles = getMatchedOrFirst(availableSubtitles, subtitlesPreferences);

    self.expose('subtitlesList', availableSubtitles);
    self.expose('subtitles', subtitles.lang_tag);

    setEnable(subtitles);
  }

  /**
   *
   * @param {string} event
   * @param {function} handler
   * @return {function(): void}
   */
  function newHandler(event, handler) {
    const safeHandler = function () {
      try {
        handler.apply(null, arguments);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          throw new Error(`Failure on execute handler for ${event} event. ${e.message}`);
        }
        self.log(`Error: ${e.message}`);
      }
    };
    _player.on(event, safeHandler);

    return () => _player.off(event, safeHandler);
  }

  function setOff() {
    setEnable({ lang_tag: OFF });
  }

  /**
   * @param {string} text
   */
  const subtitlesTextChange = (text) => {
    /**
     * @emit subtitlesTextChange
     * @property {string} text
     */
    self.expose('subtitlesTextChange', text);
  };

  /** @type {VoidFunction?} */
  let _cancelSRTResolve;

  /** @type {VoidFunction?} */
  let _removePositionUpdateListener;

  /**
   *
   * @param {Subtitle} subtitles
   */
  function setEnable(subtitles) {
    const shouldEnable = subtitles.lang_tag !== OFF;
    if (_enable === shouldEnable) {
      return;
    }

    _enable = shouldEnable;

    _removePositionUpdateListener?.();
    _removePositionUpdateListener = null;

    _cancelSRTResolve?.();
    _cancelSRTResolve = null;

    subtitlesTextChange('');

    if (!shouldEnable) {
      return;
    }

    _cancelSRTResolve = srtProvider.resolveFor(subtitles, (srtItems) => {
      const subtitleEmitter = considerStartOffset(SubtitleEmitter(srtItems, subtitlesTextChange));
      _removePositionUpdateListener = listenPositionUpdate(subtitleEmitter);
    }, (err) => {
      self.log(err);
    });
  }

  function considerStartOffset(fn) {
    const startOffset = (_player.get('playlist:info')?.startOffset ?? 0);
    return (event) => {
      const time = event.data;
      const realTime = time - startOffset;
      fn({ data: realTime });
    };
  }
}
